import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import React, { useState, useEffect } from "react";
import apiService from "../services/api.service";
import { Validator as CFValidator } from "@marketto/codice-fiscale-utils/dist/codice-fiscale-utils";
import PasswordToggle from "./PasswordToggle";
import FlashAlert from "./FlashAlert/FlashAlert";
import { ROLES, ROLE_LABELS } from "../utils/roles";
import { CROAS_OPTIONS, SICILY_PROVINCES } from "../utils/profileFields";
import "../routes/login.css";

const UserEditForm = ({ userId, onSuccess, onCancel, showCancel = false, allowRoleChange = true }) => {
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [successfulRegister, setSuccessfulRegister] = useState(true);
  const [croas, setCroas] = useState("Sicilia");
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [provincia, setProvincia] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [role, setRole] = useState("Partecipante");
  const [platformRole, setPlatformRole] = useState(ROLES.UTENTE_BASE);
  const [applicationId, setApplicationId] = useState("");
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsError, setApplicationsError] = useState("");
  const tenantRole = platformRole === ROLES.UTENTE_BASE || platformRole === ROLES.GESTORE;
  const validApplication = applications.some((item) => String(item.id) === applicationId);

  useEffect(() => {
    let active = true;
    apiService.getApplications().then(({ data }) => {
      if (active) setApplications(data || []);
    }).catch(() => {
      if (active) setApplicationsError("Impossibile caricare le applicazioni. Ricarica la pagina.");
    }).finally(() => { if (active) setApplicationsLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!userId) return;
    const loadUser = async () => {
      setLoading(true);
      try {
        const loaded = await apiService.getUserById(userId);
        const data = loaded.data;
        setUsername(data.username);
        setFirstname(data.first_name || "");
        setLastname(data.last_name || "");
        setCroas(data.croas || "Sicilia");
        setCodiceFiscale(data.codice_fiscale || "");
        setProvincia(data.provincia || "");
        setIsVerified(Boolean(data.is_verified));
        setPlatformRole(data.platform_role || ROLES.UTENTE_BASE);
        setApplicationId(data.application_id == null ? "" : String(data.application_id));
        try {
          const roleResponse = await apiService.getUserRole(userId);
          setRole(roleResponse.data.role || "Partecipante");
        } catch {
          setRole("Partecipante");
        }
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [userId]);

  const checkPasswordMismatch = () => {
    if (passwordConfirm === "") return password !== "";
    return passwordConfirm !== password;
  };

  const checkCF = () => {
    if (codiceFiscale) return CFValidator.codiceFiscale(codiceFiscale).valid;
    return false;
  };

  const checkSicily = () => croas === "Sicilia";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);
    setErrorMessage("");
    setFormSubmitted(false);
    if (tenantRole && (applicationsLoading || !validApplication)) {
      setFormSubmitted(true);
      setSuccessfulRegister(false);
      setButtonDisabled(false);
      setErrorMessage("Seleziona un'applicazione per questo account.");
      return;
    }
    try {
      const response = await apiService.editUser(
        username,
        password,
        firstname,
        lastname,
        croas,
        codiceFiscale,
        provincia,
        userId,
        isVerified,
        platformRole,
        tenantRole ? Number(applicationId) : null
      );
      await apiService.setUserRole(userId, role);
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      setFormSubmitted(true);
      setSuccessfulRegister(false);
      setButtonDisabled(false);
      if (error.response?.data?.detail === "Username already exists") {
        setErrorMessage(
          "Questa email risulta già registrata, se non riesci ad effettuare l'accesso con i tuoi dati, contatta infoceformedsrl@gmail.com"
        );
      }
    }
  };

  if (loading) {
    return <p className="users-loading">Caricamento dati utente...</p>;
  }

  return (
    <Form onSubmit={handleSubmit} className="login-form">
      {formSubmitted && !successfulRegister && (
        <FlashAlert variant="danger">
          {errorMessage || "Errore durante la modifica."}
        </FlashAlert>
      )}
      <Form.Group className="mb-3" controlId="editFirstName">
        <Form.Label>Nome</Form.Label>
        <Form.Control required value={firstname} onChange={(e) => setFirstname(e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="editLastName">
        <Form.Label>Cognome</Form.Label>
        <Form.Control required value={lastname} onChange={(e) => setLastname(e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="editEmail">
        <Form.Label>Email</Form.Label>
        <Form.Control required type="email" value={username} onChange={(e) => setUsername(e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="editPassword">
        <Form.Label>Password</Form.Label>
        <PasswordToggle
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Lascia vuoto per non modificarla"
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="editPasswordConfirm">
        <Form.Label>Conferma password</Form.Label>
        <PasswordToggle
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          placeholder="Conferma password"
          isValid={password === "" ? false : !checkPasswordMismatch()}
          isInvalid={checkPasswordMismatch()}
        />
        <Form.Control.Feedback type="invalid">Le password non corrispondono</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="editCodiceFiscale">
        <Form.Label>Codice fiscale</Form.Label>
        <Form.Control
          required
          value={codiceFiscale}
          onChange={(e) => setCodiceFiscale(e.target.value)}
          isValid={codiceFiscale === "" ? false : checkCF()}
          isInvalid={codiceFiscale === "" ? false : !checkCF()}
        />
        <Form.Control.Feedback type="invalid">Codice fiscale non valido</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="editCroas">
        <Form.Label>CROAS di appartenenza</Form.Label>
        <Form.Select
          value={croas}
          onChange={(e) => {
            setCroas(e.target.value);
            if (e.target.value !== "Sicilia") setProvincia("Altra Provincia");
            else setProvincia("");
          }}
        >
          {CROAS_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3" controlId="editProvincia">
        <Form.Label>Provincia di residenza</Form.Label>
        <Form.Select
          required
          value={provincia}
          onChange={(e) => setProvincia(e.target.value)}
          disabled={!checkSicily()}
        >
          <option disabled value="">Provincia</option>
          {SICILY_PROVINCES.map((option) => (
            <option key={option} value={option} disabled={option === "Altra Provincia" && checkSicily()}>
              {option}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3" controlId="editPlatformRole">
        <Form.Label>Ruolo piattaforma</Form.Label>
        <Form.Select value={platformRole} disabled={!allowRoleChange} onChange={(e) => setPlatformRole(e.target.value)}>
          {Object.values(ROLES).map((value) => (
            <option key={value} value={value}>{ROLE_LABELS[value]}</option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3" controlId="editApplication">
        <Form.Label>Applicazione</Form.Label>
        <Form.Select required={tenantRole} value={tenantRole ? applicationId : ""} onChange={(e) => setApplicationId(e.target.value)} disabled={!tenantRole || applicationsLoading}>
          <option value="">{tenantRole ? "Seleziona applicazione" : "Account di piattaforma"}</option>
          {applications.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Form.Select>
        {applicationsError && tenantRole && <FlashAlert variant="danger">{applicationsError}</FlashAlert>}
      </Form.Group>
      <Form.Group className="mb-3" controlId="editEventRole">
        <Form.Label>Ruolo evento (webinar)</Form.Label>
        <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="Partecipante">Partecipante</option>
          <option value="Docente">Docente</option>
          <option value="Tutor">Tutor</option>
          <option value="Revisore Fonarcom">Revisore Fonarcom</option>
          <option value="Revisore Spresal">Revisore Spresal</option>
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="edit-verify-switch"
          label={isVerified ? "Email verificata" : "Email non verificata"}
          checked={isVerified}
          onChange={() => setIsVerified((prev) => !prev)}
        />
      </Form.Group>
      <div className="users-edit-modal__actions">
        {showCancel && (
          <Button variant="outline-primary" type="button" onClick={onCancel} disabled={buttonDisabled}>
            Annulla
          </Button>
        )}
        <Button
          variant="primary"
          className="users-edit-btn"
          type="submit"
          disabled={checkPasswordMismatch() || !checkCF() || buttonDisabled || (tenantRole && (applicationsLoading || !validApplication))}
        >
          {buttonDisabled ? "Salvataggio..." : "Salva modifiche"}
        </Button>
      </div>
    </Form>
  );
};

export default UserEditForm;
